import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "eqx_compliance_token";

const formatDetail = (detail) => {
  if (detail == null) return "Node unreachable. Retry.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const STATUS_STYLES = {
  PENDING: "border-zinc-500 text-zinc-300",
  CLEARED: "border-green-500 text-green-400",
  DENIED: "border-red-500 text-red-400",
};

export default function Compliance() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [passcode, setPasscode] = useState("");
  const [requests, setRequests] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (tok) => {
      try {
        const hdrs = { headers: { Authorization: `Bearer ${tok}` } };
        const { data } = await axios.get(`${API}/compliance/requests`, hdrs);
        setRequests(data.requests);
        const auditRes = await axios.get(`${API}/compliance/audit`, hdrs);
        setAudit(auditRes.data.entries);
      } catch (e) {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
          toast.error("SESSION EXPIRED", { description: "Re-enter the compliance passcode." });
        } else {
          toast.error("LOAD FAILED", { description: formatDetail(e?.response?.data?.detail) });
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    if (!passcode.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/compliance-login`, { passcode: passcode.trim() });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPasscode("");
    } catch (err) {
      toast.error("ACCESS DENIED", { description: formatDetail(err?.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/compliance/requests/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`MARKED ${status}`);
    } catch (e) {
      toast.error("UPDATE FAILED", { description: formatDetail(e?.response?.data?.detail) });
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setRequests(null);
  };

  const sendInvite = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/compliance/requests/${id}/invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load(token);
      if (data.email_dispatched) {
        toast.success("INVITE SENT", { description: "Data room clearance email dispatched." });
      } else {
        toast.success("INVITE LOGGED", { description: "Email delivery pending Resend key activation." });
      }
    } catch (e) {
      toast.error("INVITE FAILED", { description: formatDetail(e?.response?.data?.detail) });
    }
  };

  const sendDecline = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/compliance/requests/${id}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load(token);
      if (data.email_dispatched) {
        toast.success("NOTICE SENT", { description: "Polite decline email dispatched." });
      } else {
        toast.success("NOTICE LOGGED", { description: "Email delivery pending Resend key activation." });
      }
    } catch (e) {
      toast.error("NOTICE FAILED", { description: formatDetail(e?.response?.data?.detail) });
    }
  };

  const [showPassPanel, setShowPassPanel] = useState(false);
  const [passForm, setPassForm] = useState({ current: "", next: "", confirm: "" });
  const [passBusy, setPassBusy] = useState(false);

  const changePasscode = async (e) => {
    e.preventDefault();
    if (passBusy) return;
    if (passForm.next.length < 8) {
      toast.error("PASSCODE TOO SHORT", { description: "Minimum 8 characters." });
      return;
    }
    if (passForm.next !== passForm.confirm) {
      toast.error("MISMATCH", { description: "New passcodes do not match." });
      return;
    }
    setPassBusy(true);
    try {
      await axios.post(`${API}/auth/compliance-passcode`, {
        current_passcode: passForm.current,
        new_passcode: passForm.next,
      });
      toast.success("PASSCODE UPDATED", { description: "Use the new passcode at next sign-in." });
      setPassForm({ current: "", next: "", confirm: "" });
      setShowPassPanel(false);
    } catch (err) {
      toast.error("UPDATE REJECTED", { description: formatDetail(err?.response?.data?.detail) });
    } finally {
      setPassBusy(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] text-white min-h-screen">
      <Seo title="Compliance Desk — Equinox Infrastructure Holdings" siteName="Equinox Infrastructure Holdings" />
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="compliance-back-link"
            className="font-display text-[11px] font-extrabold uppercase tracking-tight text-white hover:text-zinc-300 transition-colors"
          >
            ← Equinox Infrastructure Holdings, LLC
          </Link>
          {token && (
            <button
              data-testid="compliance-logout-btn"
              onClick={logout}
              className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 px-4 py-2 hover:bg-white hover:text-black transition-colors"
            >
              End Session
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {!token ? (
          <div className="max-w-md mx-auto mt-10">
            <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-3">
              Restricted / Compliance Desk
            </p>
            <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight font-bold">
              Clearance Required
            </h1>
            <p className="mt-4 text-xs leading-relaxed text-zinc-400">
              Data room intake records are restricted to authorized compliance personnel.
            </p>
            <form onSubmit={login} className="mt-8 space-y-4">
              <input
                type="password"
                data-testid="compliance-passcode-input"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="COMPLIANCE PASSCODE"
                className="w-full bg-[#111] border border-zinc-700 px-4 py-3 text-sm tracking-[0.2em] placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors"
              />
              <button
                type="submit"
                data-testid="compliance-login-btn"
                disabled={busy}
                className="w-full bg-white text-black font-display font-bold uppercase tracking-[0.15em] text-xs px-6 py-4 hover:bg-transparent hover:text-white border border-white transition-colors disabled:opacity-50"
              >
                {busy ? "Verifying…" : "Authenticate"}
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="compliance-dashboard">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-3">
                  Compliance Desk / Intake Records
                </p>
                <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight font-bold">
                  Data Room Requests
                </h1>
              </div>
              <button
                data-testid="compliance-passcode-toggle"
                onClick={() => setShowPassPanel((v) => !v)}
                className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 px-4 py-2 hover:bg-white hover:text-black transition-colors"
              >
                Change Passcode
              </button>
              <button
                data-testid="compliance-refresh-btn"
                onClick={() => load(token)}
                className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 px-4 py-2 hover:bg-white hover:text-black transition-colors"
              >
                Refresh
              </button>
            </div>

            {showPassPanel && (
              <form
                onSubmit={changePasscode}
                data-testid="passcode-change-panel"
                className="mb-10 border border-zinc-800 bg-[#111] p-6 grid grid-cols-1 sm:grid-cols-4 gap-3"
              >
                <input
                  type="password"
                  data-testid="passcode-current-input"
                  value={passForm.current}
                  onChange={(e) => setPassForm((f) => ({ ...f, current: e.target.value }))}
                  placeholder="CURRENT PASSCODE"
                  className="bg-[#0A0A0A] border border-zinc-700 px-4 py-3 text-xs tracking-[0.15em] placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors"
                />
                <input
                  type="password"
                  data-testid="passcode-new-input"
                  value={passForm.next}
                  onChange={(e) => setPassForm((f) => ({ ...f, next: e.target.value }))}
                  placeholder="NEW PASSCODE (8+ CHARS)"
                  className="bg-[#0A0A0A] border border-zinc-700 px-4 py-3 text-xs tracking-[0.15em] placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors"
                />
                <input
                  type="password"
                  data-testid="passcode-confirm-input"
                  value={passForm.confirm}
                  onChange={(e) => setPassForm((f) => ({ ...f, confirm: e.target.value }))}
                  placeholder="CONFIRM NEW PASSCODE"
                  className="bg-[#0A0A0A] border border-zinc-700 px-4 py-3 text-xs tracking-[0.15em] placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors"
                />
                <button
                  type="submit"
                  data-testid="passcode-change-submit"
                  disabled={passBusy}
                  className="bg-white text-black font-display font-bold uppercase tracking-[0.15em] text-[10px] px-4 py-3 hover:bg-transparent hover:text-white border border-white transition-colors disabled:opacity-50"
                >
                  {passBusy ? "Updating…" : "Update Passcode"}
                </button>
              </form>
            )}

            {requests === null ? (
              <p className="text-xs text-zinc-500 tracking-widest uppercase">Loading records…</p>
            ) : requests.length === 0 ? (
              <p className="text-xs text-zinc-500 tracking-widest uppercase">No intake records on file.</p>
            ) : (
              <div className="border border-zinc-800 divide-y divide-zinc-800">
                {requests.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`request-row-${r.id}`}
                    className="grid grid-cols-12 gap-4 items-center px-4 sm:px-6 py-5 hover:bg-[#101010] transition-colors"
                  >
                    <div className="col-span-12 md:col-span-3">
                      <p className="font-display font-bold uppercase text-sm tracking-tight">
                        {r.first_name} {r.last_name}
                      </p>
                      <p className="text-[10px] text-zinc-500 mt-1">
                        REF {r.id.slice(0, 8).toUpperCase()} · {new Date(r.created_at).toLocaleString("en-US", { timeZone: "UTC" })} UTC
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-4 text-xs text-zinc-300 break-all">
                      {r.corporate_email}
                    </div>
                    <div className="col-span-6 md:col-span-2">
                      <span
                        data-testid={`status-pill-${r.id}`}
                        className={`inline-block text-[9px] tracking-[0.25em] uppercase border px-3 py-1 ${STATUS_STYLES[r.status || "PENDING"]}`}
                      >
                        {r.status || "PENDING"}
                      </span>
                    </div>
                    <div className="col-span-6 md:col-span-3 flex flex-wrap gap-2 md:justify-end">
                      {(r.status === "CLEARED") && (
                        <span className="flex flex-col items-start gap-1">
                          <button
                            data-testid={`invite-btn-${r.id}`}
                            onClick={() => sendInvite(r.id)}
                            className={`text-[9px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
                              r.invite_sent
                                ? "border-green-500 text-green-400 hover:bg-green-500 hover:text-black"
                                : "border-white bg-white text-black hover:bg-transparent hover:text-white"
                            }`}
                          >
                            {r.invite_sent ? "Resend Invite" : "Send Invite"}
                          </button>
                          {r.invite_sent_at && (
                            <span data-testid={`invite-date-${r.id}`} className="text-[8px] tracking-[0.2em] uppercase text-zinc-600">
                              Logged {new Date(r.invite_sent_at).toLocaleString("en-US", { timeZone: "UTC" })} UTC
                            </span>
                          )}
                        </span>
                      )}
                      {(r.status === "DENIED") && (
                        <span className="flex flex-col items-start gap-1">
                          <button
                            data-testid={`decline-btn-${r.id}`}
                            onClick={() => sendDecline(r.id)}
                            className={`text-[9px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
                              r.decline_sent
                                ? "border-green-500 text-green-400 hover:bg-green-500 hover:text-black"
                                : "border-zinc-500 text-zinc-300 hover:border-white hover:text-white"
                            }`}
                          >
                            {r.decline_sent ? "Resend Notice" : "Send Notice"}
                          </button>
                          {r.decline_sent_at && (
                            <span data-testid={`decline-date-${r.id}`} className="text-[8px] tracking-[0.2em] uppercase text-zinc-600">
                              Logged {new Date(r.decline_sent_at).toLocaleString("en-US", { timeZone: "UTC" })} UTC
                            </span>
                          )}
                        </span>
                      )}
                      {["CLEARED", "DENIED", "PENDING"].map((s) => (
                        <button
                          key={s}
                          data-testid={`status-btn-${s.toLowerCase()}-${r.id}`}
                          onClick={() => setStatus(r.id, s)}
                          className={`text-[9px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
                            (r.status || "PENDING") === s
                              ? "border-white bg-white text-black"
                              : "border-zinc-700 text-zinc-400 hover:border-white hover:text-white"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {audit.length > 0 && (
              <div data-testid="audit-trail" className="mt-14">
                <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-4">
                  Activity Log
                </p>
                <div className="border border-zinc-800 divide-y divide-zinc-800">
                  {audit.map((a) => (
                    <div
                      key={a.id}
                      data-testid={`audit-entry-${a.id}`}
                      className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 px-4 sm:px-6 py-3 text-[10px] tracking-[0.15em] uppercase"
                    >
                      <span className="text-zinc-600 shrink-0 tabular-nums">
                        {new Date(a.at).toLocaleString("en-US", { timeZone: "UTC" })} UTC
                      </span>
                      <span className="text-white shrink-0 sm:w-40">{a.action}</span>
                      <span className="text-zinc-500 break-all">{a.detail || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
